import { ReactNode, useState, useEffect, useCallback } from "react";
import AppSidebar from "./AppSidebar";
import MobileDesktopOnlyNotice from "./MobileDesktopOnlyNotice";
import BrandRecapModal from "@/components/discover/BrandAnalysisModal";
import { useAccountStatus } from "@/hooks/use-account-status";
import { useIsMobile } from "@/hooks/use-mobile";

interface DashboardLayoutProps {
  children: ReactNode;
}

// Set by BuildingProfilePage right before it hands off to /discover — lets
// the welcome/recap modal paint on the very first frame instead of flashing
// an empty dashboard while account status loads async. Consumed once here;
// welcomeSeen/accountStatus (below) remain the actual source of truth for
// whether the modal stays open.
const EXPECT_WELCOME_KEY = "ernest_expect_welcome_v1";

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const isMobile = useIsMobile();

  // Rendered here (DashboardLayout is eager, not lazy) rather than inside
  // Discover itself — Discover is a lazy-loaded route, so mounting the
  // modal there left a gap where the sidebar/page could paint first while
  // its chunk loaded. Here it's on screen the instant the protected shell
  // mounts, before any nested route resolves.
  //
  // This only ever shows the welcome/recap modal — the post-onboarding
  // loading animation is its own standalone route (BuildingProfilePage),
  // reached directly from OnboardingPage, with no link to this component.
  const { status: accountStatus, loading: accountStatusLoading, welcomeSeen, markWelcomeSeen } = useAccountStatus();
  // Not opened from the post-onboarding flag any more: a brand still under review already sees the same recap as the
  // page itself (CommunityAccessGate), so opening it here too stacked two popups. The flag is only cleared.
  const [introOpen, setIntroOpen] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try { sessionStorage.removeItem(EXPECT_WELCOME_KEY); } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    if (accountStatusLoading) return;
    // Approved brands get the welcome on their first visit.
    if (accountStatus === "accepted" && !welcomeSeen) {
      setIntroOpen(true);
    }
  }, [accountStatus, accountStatusLoading, welcomeSeen]);
  const closeIntro = useCallback(() => {
    setIntroOpen(false);
    if (accountStatus === "accepted") {
      void markWelcomeSeen();
    }
  }, [accountStatus, markWelcomeSeen]);

  if (isMobile) {
    return <MobileDesktopOnlyNotice />;
  }

  return (
    <div className="min-h-screen bg-background flex">
      <BrandRecapModal open={introOpen} onClose={closeIntro} />
      <AppSidebar />
      {/* Main content — offset for sidebar */}
      <div className="flex-1 min-w-0 ml-36 min-h-screen">
        {children}
      </div>
    </div>
  );
};

export default DashboardLayout;
